import { Box, Divider, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { GraphLineChart } from '../../analytics-components/graphs/graph-line-chart'
import { SrsMetricsFigures } from './srs-metrics-figures'
import { ISrsFigures, ISrsPublicStructure, SrsSectionKey } from '../../../../shared/services/srs-report/srs-report.model'

/**
 * FALLBACK section and heading structure for the READ-ONLY rendered document, mirroring
 * gaia-carbon-be/backend/lib/srs/srsS2.constants.js (SRS_SECTIONS + NARRATIVE_FIELDS headings).
 *
 * The wizard never uses this: its steps are driven entirely by GET srs-report/structure, and the
 * document prefers the `structure` block on its data when one is supplied (the public share payload
 * now carries it). This copy only covers payloads shared before that field was added. If a heading
 * changes in the backend constants, change it here in the same piece of work.
 */
const DOCUMENT_SECTIONS: { label: string, fields: { field: string, heading: string }[] }[] = [
  {
    label: 'Governance',
    fields: [
      { field: 'governanceOversightNarrative', heading: 'Board oversight of climate-related risks and opportunities' },
      { field: 'governanceManagementNarrative', heading: "Management's role" },
    ],
  },
  {
    label: 'Strategy',
    fields: [
      { field: 'strategyRisksNarrative', heading: 'Climate-related risks and opportunities' },
      { field: 'strategyBusinessModelNarrative', heading: 'Effects on business model and value chain' },
      { field: 'strategyDecisionMakingNarrative', heading: 'Effects on strategy and decision-making' },
      { field: 'strategyFinancialEffectsNarrative', heading: 'Financial effects' },
      { field: 'strategyResilienceNarrative', heading: 'Climate resilience' },
    ],
  },
  {
    label: 'Risk management',
    fields: [
      { field: 'riskProcessNarrative', heading: 'Identifying, assessing and managing climate-related risks' },
      { field: 'riskIntegrationNarrative', heading: 'Integration with overall risk management' },
    ],
  },
]

const METRICS_SECTION_LABEL = 'Metrics and targets'
const TARGETS_HEADING = 'Climate-related targets'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * The document fields the rendered disclosure needs. The public share endpoint returns exactly this
 * shape (narratives flat, keyed by field name); the signed-in detail page assembles it from the
 * report record and its frozen figures.
 */
export interface ISrsDocumentData {
  organisationName: string
  organisationLogoUrl?: string | null
  generatedAt: string | null
  narratives: Record<string, string | null | undefined>
  signOffName: string | null
  signOffTitle: string | null
  signOffDate: string | null
  sectionContent?: Record<string, string> | null
  figures: ISrsFigures
  preparedWithText: string
  /**
   * Section headings and references from the backend structure. Preferred when present; the
   * DOCUMENT_SECTIONS fallback above covers payloads shared before the field existed.
   */
  structure?: ISrsPublicStructure | null
}

/**
 * The UK SRS S2 climate disclosure rendered as a document. Shared by the public share page and the
 * signed-in report view so the two can never drift apart. Renders frozen figures only; it has no
 * awareness of live data or of who is looking at it.
 */
export const SrsDocument: FunctionComponent<{ report: ISrsDocumentData }> = ({ report }) => {
  // Report-content overrides: a key in sectionContent replaces the built-in copy; blank falls back.
  const content = (key: string, fallback: string) => {
    const value = report.sectionContent?.[key]
    return typeof value === 'string' && value.trim() ? value : fallback
  }

  const heading = (text: string) => (
    <>
      <Typography variant='h6' sx={{ mt: 4, mb: 1 }}>{text}</Typography>
      <Divider sx={{ mb: 2, borderBottomWidth: 2, borderColor: 'primary.main' }} />
    </>
  )

  const narrative = (text: string | null | undefined) => (
    text
      ? <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{text}</Typography>
      : (
        <Typography variant='body2' sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 2 }}>
          Not disclosed.
        </Typography>
      )
  )

  // Prefer the backend-served structure so headings come from the same source of truth as the
  // wizard; the local constant only covers payloads shared before the structure travelled with them.
  const structureFields = report.structure?.narrativeFields || []
  const pillarOrder: SrsSectionKey[] = ['GOVERNANCE', 'STRATEGY', 'RISK_MANAGEMENT']
  const documentSections = structureFields.length
    ? pillarOrder
      .map((pillar, index) => ({
        label: report.structure?.sections?.[pillar] || DOCUMENT_SECTIONS[index].label,
        fields: structureFields
          .filter((field) => field.section === pillar)
          .map(({ field, heading }) => ({ field, heading })),
      }))
      .filter((section) => section.fields.length > 0)
    : DOCUMENT_SECTIONS
  const metricsLabel = report.structure?.sections?.METRICS_TARGETS || METRICS_SECTION_LABEL
  const targetsHeading = structureFields.find((field) => field.field === 'targetsNarrative')?.heading
    || TARGETS_HEADING

  const graph = report.figures.projectionGraph || []
  const dataset = graph.map((point) => ({
    year: String(point.year),
    actual: point.actualValue ?? point.currentValue ?? null,
    target: point.targetValue ?? null,
    projected: point.projectedValue ?? null,
  }))

  return (
    <Paper variant='outlined' sx={{ p: { xs: 3, md: 5 } }}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
        <Box>
          <Typography variant='h4' sx={{ mb: 2 }}>
            {content('title', 'Climate-related disclosures')}
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Organisation:</strong> {report.organisationName}
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Reporting period:</strong> {report.figures.periods.current?.label || '—'}
          </Typography>
          <Typography variant='body2' sx={{ mb: 2 }}>
            {content('standardLine', 'Prepared following the structure of UK SRS S2 (IFRS S2), the UK Sustainability Reporting Standard for climate-related disclosures.')}
          </Typography>
        </Box>
        {/* Fixed-height box so the header does not reflow as the image loads. */}
        {report.organisationLogoUrl && (
          <Box sx={{ height: 56, flexShrink: 0 }}>
            <img
              src={report.organisationLogoUrl}
              alt={`${report.organisationName} logo`}
              style={{ maxHeight: 56, maxWidth: 180, objectFit: 'contain' }}
            />
          </Box>
        )}
      </Stack>

      {documentSections.map((section) => (
        <Box key={section.label}>
          {heading(section.label)}
          {section.fields.map((field) => (
            <Box key={field.field}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>{field.heading}</Typography>
              {narrative(report.narratives[field.field])}
            </Box>
          ))}
        </Box>
      ))}

      {heading(metricsLabel)}
      <SrsMetricsFigures figures={report.figures} />

      <Typography variant='subtitle2' sx={{ mb: 1 }}>{targetsHeading}</Typography>
      {narrative(report.narratives.targetsNarrative)}

      <SrsReductionsBlock figures={report.figures} />

      {dataset.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <GraphLineChart
            title='Emissions against target'
            tooltip='Organisation-wide emissions against target, as recorded in the reduction plan.'
            dataset={dataset}
            dataKey='year'
            series={[
              { dataKey: 'actual', label: 'Actual tCO2e' },
              { dataKey: 'target', label: 'Target tCO2e' },
              { dataKey: 'projected', label: 'Projected tCO2e' },
            ]}
            colors={[gaiaTheme.palette.primary.main, gaiaTheme.palette.error.main, gaiaTheme.palette.info.main]}
          />
        </Box>
      )}

      {heading('Approval')}
      <Typography variant='body2' sx={{ mb: 2 }}>
        These disclosures were approved on behalf of the organisation by:
      </Typography>
      <Typography variant='body2'>
        {report.signOffName || ''}{report.signOffTitle ? `, ${report.signOffTitle}` : ''}
      </Typography>
      <Typography variant='body2' sx={{ mt: 1 }}>
        <strong>Date:</strong>{' '}
        {report.signOffDate ? moment(report.signOffDate).format('D MMMM YYYY') : '—'}
      </Typography>

      <Divider sx={{ my: 4 }} />
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
        {report.preparedWithText}
      </Typography>
      {report.generatedAt && (
        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
          Generated on {moment(report.generatedAt).format('D MMMM YYYY')}.
        </Typography>
      )}
    </Paper>
  )
}

/** Targets and progress, plan coverage and the offsets register, mirroring the generated artefact. */
const SrsReductionsBlock: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const reductions = figures.reductions
  if (!reductions) return null

  const paragraphs: string[] = []

  const settings = reductions.targets?.settings
  if (settings) {
    const pctRows: [string, number | null | undefined][] = [
      ['Scope 1', settings.scopeOneReductionPercentage],
      ['Scope 2 (location-based)', settings.scopeTwoLocationReductionPercentage],
      ['Scope 2 (market-based)', settings.scopeTwoMarketReductionPercentage],
      ['Scope 3', settings.scopeThreeReductionPercentage],
    ]
    const active = pctRows.filter(([, value]) => value != null && Number(value) !== 0)
    if (active.length) {
      const spans = active.map(([label, value]) => `${label} by ${Number(value)}%`).join(', ')
      const base = settings.baseYear ? ` against a ${settings.baseYear} base year` : ''
      const targetYear = settings.targetYear ? ` by ${settings.targetYear}` : ''
      paragraphs.push(`The organisation has committed to reducing ${spans}${targetYear}${base}.`)
    }
  }

  const coverage = reductions.planCoverage
  if (coverage) {
    const bits: string[] = []
    if (coverage.totalActions != null) bits.push(`${coverage.totalActions} committed reduction actions`)
    if (coverage.completedActions != null) bits.push(`${coverage.completedActions} completed`)
    if (coverage.quantifiedActions != null) bits.push(`${coverage.quantifiedActions} with a quantified saving`)
    if (coverage.quantifiedAnnualReductionTco2e != null) {
      bits.push(`a combined quantified annual reduction of ${fmt(coverage.quantifiedAnnualReductionTco2e)} tCO2e`)
    }
    if (bits.length) paragraphs.push(`The reduction plan holds ${bits.join(', ')}.`)
  }

  const offsetRows = reductions.offsets?.rows || []

  if (!paragraphs.length && !offsetRows.length) return null

  return (
    <Box sx={{ mb: 3 }}>
      {paragraphs.map((paragraph) => (
        <Typography variant='body2' sx={{ mb: 1.5 }} key={paragraph}>{paragraph}</Typography>
      ))}
      {offsetRows.length > 0 && (
        <>
          <Typography variant='subtitle2' sx={{ mt: 2, mb: 1 }}>Carbon credits</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Credit</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Registry</TableCell>
                  <TableCell>Vintage</TableCell>
                  <TableCell align='right'>tCO2e</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offsetRows.map((offset, index) => (
                  <TableRow key={`${offset.name || 'offset'}-${index}`}>
                    <TableCell>{offset.name || ''}</TableCell>
                    <TableCell>{offset.offsetType || ''}</TableCell>
                    <TableCell>{offset.registry || ''}</TableCell>
                    <TableCell>{offset.vintage != null ? String(offset.vintage) : ''}</TableCell>
                    <TableCell align='right'>{fmt(Number(offset.amountTCE ?? 0))}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontFamily: 'EffraMd' }}>Total</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'EffraMd' }}>
                    {fmt(Number(reductions.offsets?.totalTco2e ?? 0))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
          <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
            Reported emissions totals are gross. Carbon credits are disclosed separately and are never
            netted against emissions.
          </Typography>
        </>
      )}
    </Box>
  )
}
